import { useEffect } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Text,
  Block,
  Button,
  Form,
  FormCard,
  Input,
  Spinner,
  CustomLink,
  PasswordInput,
  Anchor,
} from '../../../components';
import { DispatchAction, DefaultState } from '../../../redux/store';
import { login } from '../../../redux/user/user.thunks';
import { selectUserState } from '../../../redux/user/user.selectors';
import { UserState } from '../../../redux/user/user.slice';
import { routes } from '../../../routes';
import { isValidEmail, isValidPassword } from '../../../modules/user/domain/User';

export interface SingInFormikState {
  email: string;
  password: string;
}

const SingInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = (location.state as { from?: string })?.from || '/';
  const { user, isLoading } = useSelector<DefaultState, UserState>(selectUserState);
  const dispatch = useDispatch<DispatchAction>();

  useEffect(() => {
    if (user?.isConfirmed) navigate(redirectPath);
  }, [user, history, redirectPath]);

  const initialState: SingInFormikState = {
    email: '',
    password: '',
  };

  const formik = useFormik({
    initialValues: initialState,
    onSubmit: async (values) => {
      const resultAction = await dispatch(login({ userData: values }));

      if (login.fulfilled.match(resultAction)) {
        navigate(redirectPath);
      }
    },
    validate: (values: SingInFormikState) => {
      const errors: Partial<SingInFormikState> = {};
      if (!values.email) {
        errors.email = 'Email requerido';
      } else if (!isValidEmail(values.email)) {
        errors.email = 'Email invalido';
      }
      if (!values.password) {
        errors.password = 'Campo requerido';
      } else if (!isValidPassword(values.password)) {
        errors.password = 'Contraseña minimo 5 caracteres';
      }
      return errors;
    },
  });

  if (isLoading || formik.isSubmitting) return <Spinner top="25%" />;
  const isButtonDisabled = !formik.dirty || formik.isSubmitting || !formik.isValid;

  return (
    <Form
      onSubmit={formik.handleSubmit}
      display="flex"
      direction="column"
      justify="space-evenly"
      align="center"
      width="100%"
      pt="xl"
      pb="xl"
    >
      <FormCard>
        <Block width="100%" display="flex" direction="column" align="center" justify="center">
          <Block display="flex" width="100%" justify="center" pt="s" pb="s">
            <Text color="black" as="h3" weight="semiBold" fontsize="s" isUppercase>
              Entrar
            </Text>
          </Block>
          <Block display="flex" direction="column" width="100%" pt="s" pb="s">
            <Text as="h4" fontsize="s">
              Email
            </Text>
            <Input
              id="login_email_input"
              onChange={formik.handleChange}
              type="email"
              name="email"
              value={formik.values.email}
              variant="outlined"
              inputSize="m"
            />
            {formik.errors.email && (
              <Block display="flex" width="100%" justify="center">
                <Text fontsize="s" color="red">
                  {formik.errors.email}
                </Text>
              </Block>
            )}
          </Block>
          <Block display="flex" direction="column" width="100%" pt="s" pb="s">
            <Text as="h4" fontsize="s">
              Contraseña
            </Text>
            <PasswordInput
              id="login_password_input"
              onChange={formik.handleChange}
              name="password"
              value={formik.values.password}
            />
            {formik.errors.password && (
              <Block display="flex" width="100%" justify="center">
                <Text fontsize="s" color="red">
                  {formik.errors.password}
                </Text>
              </Block>
            )}
          </Block>
          <Block display="flex" direction="column" align="flex-end" width="100%">
            <Block display="flex" justify="center" pt="m" pb="m" width="100%">
              <Button
                text="Aceptar"
                type="submit"
                disabled={isButtonDisabled}
                variant="default"
                color="xLightSilver"
                size="m"
                fullWidth
                id="sign_in_accept"
              />
            </Block>
            <Block display="flex" width="100%" pt="m" pb="m">
              <Anchor href={`${routes.GOOGLE_LOGIN}`} display="flex" width="100%">
                <Button
                  text="Continuar con Google"
                  size="m"
                  variant="default"
                  color="xLightSilver"
                  icon="google"
                  iconSize="s"
                  fullWidth
                  id="sign_in_with_google"
                  onClick={() => null}
                />
              </Anchor>
            </Block>
            <Block display="flex" justify="center" pb="s">
              <CustomLink fontSize="m" hoverColor="darkSilver" to={routes.FORGOT_PASSWORD} align="center">
                ¿Olvido su contraseña?
              </CustomLink>
            </Block>
            <Block display="flex" justify="center" pb="s">
              <CustomLink fontSize="m" hoverColor="darkSilver" to={routes.REGISTER} align="center">
                ¿Aun no tienes cuenta?
              </CustomLink>
            </Block>
          </Block>
        </Block>
      </FormCard>
    </Form>
  );
};

export default SingInPage;
